import { inject, Injectable, Injector, OnDestroy, runInInjectionContext } from '@angular/core';
import { getToken, Messaging, onMessage, Unsubscribe } from '@angular/fire/messaging';
import Dexie from 'dexie';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private messaging = inject(Messaging);
  private injector = inject(Injector);

  unsubscribeToMessages: Unsubscribe;
  unsubscribeToMessagesBackground: Unsubscribe;
  db = new ContentDB();
  private notificationSubject = new BehaviorSubject<any>({});
  notification$ = this.notificationSubject.asObservable();

  private embeddingGenerationCompleteSubject = new Subject<string>();
  embeddingGenerationComplete$ = this.embeddingGenerationCompleteSubject.asObservable();

  private newChunkReceivedSubject = new Subject<any>();
  newChunkReceived$ = this.newChunkReceivedSubject.asObservable();

  private videoAnalysisCompleteSubject = new Subject<any>();
  videoAnalysisComplete$ = this.videoAnalysisCompleteSubject.asObservable();

  private videoProcedureCompleteSubject = new Subject<any>();
  videoProcedureComplete$ = this.videoProcedureCompleteSubject.asObservable();

  sendNotification(notification) {
    this.notificationSubject.next(notification);
  }

  processPreviousNotifications() {
    const notifications = this.getAllNotifications();
  }

  listenToMessages() {
    this.unsubscribeToMessages = onMessage(this.messaging, (payload) => {
      console.log('Received FCM message:', payload);
      const data = payload.data as any;

      if (data && data.type) {
        this.processNotification(data);
      } else {
        console.warn('Received FCM message without a data type:', payload);
      }
    });
  }

  processNotification(data: any) {
    switch (data.type) {
      case 'embedding-generation-complete':
        console.log('Processing embedding-generation-complete notification for contentId:', data.contentId);
        this.embeddingGenerationCompleteSubject.next(data.contentId);
        break;

      case 'new-chunk':
      case 'chunk-updated':
        console.log(`Processing ${data.type} notification for contentId:`, data.contentID, data);
        this.newChunkReceivedSubject.next(data);
        break;

      case 'video-analysis-complete':
      case 'image-analysis-complete':
        console.log(`Processing ${data.type} notification for contentId:`, data.contentId, data);
        this.videoAnalysisCompleteSubject.next(data);
        break;

      case 'video-procedure-complete':
        console.log(`Processing ${data.type} notification for contentId:`, data.contentId, data);
        this.videoProcedureCompleteSubject.next(data);
        break;

      // You can add more cases here for other notification types
      // case 'another-notification-type':
      //   // Handle another type
      //   break;

      default:
        console.warn('Unhandled notification type:', data.type);
        // Optionally, send a generic notification to the subject
        this.sendNotification(data);
        break;
    }
  }

  async getNotificationToken() {
    try {
      if (Notification.permission !== 'granted') {
        // FCM Disabled
        // await Notification.requestPermission();
      }
      const registration = await navigator.serviceWorker.getRegistration();

      const currentToken = await runInInjectionContext(this.injector, () =>
        getToken(this.messaging, {
          vapidKey: environment.publicVapidKey,
          serviceWorkerRegistration: registration,
        })
      );

      if (currentToken) {
        const storedToken = localStorage.getItem('notificationToken');
        if (storedToken !== currentToken) {
          localStorage.setItem('notificationToken', currentToken);
        }
        return currentToken;
      } else {
        console.log('unable to get notification token');
        throw new Error('Unable to get notification token');
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  async getAllNotifications() {
    return new Promise((resolve) => {
      this.db
        .transaction('rw', this.db['notifications'], async () => {
          let data = await this.db['notifications'].toArray();
          resolve(data);
        })
        .catch((e) => {
          resolve([]);
          console.error(e.stack || e);
        });
    });
  }

  ngOnDestroy(): void {
    if (this.unsubscribeToMessages) this.unsubscribeToMessages();
  }
}

class ContentDB extends Dexie {
  public notifications: Dexie.Table<any, string>;
  public constructor() {
    super('notifications-db');
    this.version(23).stores({
      notifications: '++_id,_ts',
    });
    this.notifications = this.table('notifications');
  }
}
